import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Modal from '../Modal';
import './TaskModal.css';

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || 'https://agent-book-crm-backend.onrender.com';

function TaskModal({ task: initialTask, onClose, onUpdate, source }) {
  const [task, setTask] = useState({
    ...initialTask,
    start_time: initialTask.start_time?.slice(0, 5) || '',
    end_time: initialTask.end_time?.slice(0, 5) || '',
    phone_number: initialTask.phone_number || initialTask.phoneNumber || '',
  });

  const [showNoteInput, setShowNoteInput] = useState(Boolean(initialTask.note));
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const navigate = useNavigate();

  const fmtDateInput = (iso) => iso?.split('T')[0] || '';

  const normalizedType = task.type || 'Task';

  const showEndTime = useMemo(
    () =>
      normalizedType === 'Phone Call' ||
      normalizedType === 'Meeting' ||
      normalizedType === 'Appointment',
    [normalizedType]
  );

  const showPhoneNumber = useMemo(
    () => normalizedType === 'Phone Call' || normalizedType === 'Meeting',
    [normalizedType]
  );

  const modalCopy = useMemo(() => {
    if (normalizedType === 'Phone Call') {
      return {
        eyebrow: 'Phone Call',
        title: 'Phone Call',
        subtitle: 'Review or update this scheduled call.',
        dateLabel: 'Call Date',
        startLabel: 'Call Start Time',
        endLabel: 'Call End Time',
        noteLabel: 'Call Notes',
        notePlaceholder: 'Enter call notes…',
      };
    }

    if (normalizedType === 'Meeting') {
      return {
        eyebrow: 'Meeting',
        title: 'Meeting',
        subtitle: 'Review or update this scheduled meeting.',
        dateLabel: 'Meeting Date',
        startLabel: 'Meeting Start Time',
        endLabel: 'Meeting End Time',
        noteLabel: 'Meeting Notes',
        notePlaceholder: 'Enter meeting notes, location, or link…',
      };
    }

    if (normalizedType === 'Appointment') {
      return {
        eyebrow: 'Appointment',
        title: 'Appointment',
        subtitle: 'Review or update this scheduled appointment.',
        dateLabel: 'Appointment Date',
        startLabel: 'Appointment Start Time',
        endLabel: 'Appointment End Time',
        noteLabel: 'Appointment Notes',
        notePlaceholder: 'Enter appointment notes…',
      };
    }

    return {
      eyebrow: 'Task',
      title: normalizedType,
      subtitle: 'Review and update this task.',
      dateLabel: 'Date',
      startLabel: 'Start Time',
      endLabel: 'End Time',
      noteLabel: 'Note',
      notePlaceholder: 'Enter note…',
    };
  }, [normalizedType]);

  const shouldShowProfileLink =
    (source === 'dashboard' || source === 'calendar') && !!task.full_name;

  const saveChanges = async () => {
    try {
      setSaving(true);

      const res = await fetch(`${API_BASE_URL}/api/tasks/${task.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({
          status: task.status,
          dueDate: task.due_date?.split('T')[0],
          startTime: task.start_time ? `${task.start_time}:00` : null,
          endTime: showEndTime && task.end_time ? `${task.end_time}:00` : null,
          phoneNumber: showPhoneNumber ? task.phone_number : '',
          note: task.note,
        }),
      });

      if (!res.ok) {
        const errorText = await res.text();
        throw new Error(errorText || 'Error updating task');
      }

      onUpdate?.();
      onClose?.();
    } catch (err) {
      console.error('❌ Error updating task:', err);
      alert('Error updating task');
    } finally {
      setSaving(false);
    }
  };

  const deleteTask = async () => {
    if (!window.confirm('Delete this task permanently?')) return;

    try {
      setDeleting(true);

      const res = await fetch(`${API_BASE_URL}/api/tasks/${task.id}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (!res.ok) {
        const errorText = await res.text();
        throw new Error(errorText || 'Error deleting task');
      }

      onUpdate?.();
      onClose?.();
    } catch (err) {
      console.error('❌ Error deleting task:', err);
      alert('Error deleting task');
    } finally {
      setDeleting(false);
    }
  };

  const goToProfile = () => {
    const id = task.lead_id || task.client_id;
    const path = task.lead_id ? 'lead' : 'client';

    if (!id) return;

    navigate(`/${path}-profile/${id}`);
    onClose?.();
  };

  return (
    <Modal onClose={onClose} size="sm" className="task-modal-shell">
      <div className="ab-modal-header task-modal-header">
        <div className="ab-modal-header-content">
          <p className="ab-modal-eyebrow">{modalCopy.eyebrow}</p>
          <h2 className="ab-modal-title">{modalCopy.title}</h2>
          <p className="ab-modal-subtitle">{modalCopy.subtitle}</p>
        </div>
      </div>

      <div className="ab-modal-body task-modal-body">
        <div className="task-title-card">
          <div className="task-title-card-top">
            <div>
              <div className="task-title-label">Title</div>
              <div className="task-title-value">{task.title || '—'}</div>
            </div>

            {shouldShowProfileLink && (
              <button
                type="button"
                className="task-profile-link"
                onClick={goToProfile}
              >
                {task.full_name} ↗
              </button>
            )}
          </div>
        </div>

        <div className="task-form-layout">
          <div className="task-row task-row-two">
            <div className="ab-field">
              <label>Status</label>
              <select
                value={task.status || ''}
                onChange={(e) => setTask({ ...task, status: e.target.value })}
              >
                <option value="">—</option>
                <option value="Pending">Pending</option>
                <option value="In Progress">In Progress</option>
                <option value="Completed">Completed</option>
                <option value="Rescheduled">Rescheduled</option>
                <option value="Cancelled">Cancelled</option>
              </select>
            </div>

            <div className="ab-field">
              <label>{modalCopy.dateLabel}</label>
              <input
                type="date"
                value={fmtDateInput(task.due_date)}
                onChange={(e) => {
                  const newDate = e.target.value;
                  setTask((prev) => ({
                    ...prev,
                    due_date: newDate ? `${newDate}T00:00:00` : '',
                  }));
                }}
              />
            </div>
          </div>

          <div className={`task-row ${showEndTime ? 'task-row-two' : 'task-row-one'}`}>
            <div className="ab-field">
              <label>{modalCopy.startLabel}</label>
              <input
                type="time"
                value={task.start_time}
                onChange={(e) =>
                  setTask((prev) => ({
                    ...prev,
                    start_time: e.target.value,
                  }))
                }
              />
            </div>

            {showEndTime && (
              <div className="ab-field">
                <label>{modalCopy.endLabel}</label>
                <input
                  type="time"
                  value={task.end_time}
                  onChange={(e) =>
                    setTask((prev) => ({
                      ...prev,
                      end_time: e.target.value,
                    }))
                  }
                />
              </div>
            )}
          </div>

          {showPhoneNumber && (
            <div className="ab-field ab-field-full">
              <label>Phone Number</label>
              <input
                type="text"
                value={task.phone_number || ''}
                onChange={(e) =>
                  setTask((prev) => ({
                    ...prev,
                    phone_number: e.target.value,
                  }))
                }
                placeholder="Enter phone number"
              />
            </div>
          )}

          <div className="ab-field ab-field-full">
            <label>{modalCopy.noteLabel}</label>

            {!showNoteInput ? (
              <div className="task-note-card">
                <div className="task-note-text">{task.note || 'No note added.'}</div>
                <button
                  type="button"
                  className="task-inline-link"
                  onClick={() => setShowNoteInput(true)}
                >
                  + Add note
                </button>
              </div>
            ) : (
              <textarea
                value={task.note || ''}
                onChange={(e) => setTask({ ...task, note: e.target.value })}
                placeholder={modalCopy.notePlaceholder}
                rows={4}
              />
            )}
          </div>
        </div>
      </div>

      <div className="ab-modal-footer ab-modal-footer-split task-modal-footer">
        <button
          type="button"
          className="ab-btn ab-btn-danger"
          onClick={deleteTask}
          disabled={deleting}
        >
          {deleting ? 'Deleting...' : 'Delete'}
        </button>

        <div className="ab-modal-footer-actions">
          <button
            type="button"
            className="ab-btn ab-btn-secondary"
            onClick={onClose}
          >
            Close
          </button>

          <button
            type="button"
            className="ab-btn ab-btn-primary"
            onClick={saveChanges}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

export default TaskModal;